import { cloneDeep } from 'lodash';
import Vue from 'vue';
import registerVueGlobals from '@/registerVueGlobals';
import App from '@/views/App.vue';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginVue from '@bugsnag/plugin-vue';
import '@/assets/scss/general.scss';
import '@/assets/mdi.css';
import initVuetify from '@/plugins/vuetify';
import getLimiters from '@/inputCharsLimiters';
import getConstants from '@/constants';
import getHelpers from '@/helpers';
import { initValidation } from '@/validation';
import createApi from '@/services/api';
import VueGtm from 'vue-gtm';
import { initRumTracker } from '@/plugins/Site24x7RumTracker';
import {
  DEFAULT_WL_CONFIG,
  processConfig,
  initPartnerFonts,
} from '@/config/common';
import store from '@/store';
import { initRouter } from '@/router';
import { usePartnerConfig } from '@/services/usePartnerConfig';
import { AxiosError } from 'axios';

const getConfig = async (productName) => {
  const partnerConfigResponse = await Vue.prototype.$api.initForm();

  if (partnerConfigResponse.status !== 'success') throw new Error('Config is not available.');
  return processConfig(
    Object.assign(
      {},
      cloneDeep(DEFAULT_WL_CONFIG),
      partnerConfigResponse.data,
    ),
    productName,
  );
};

// This function handles any unhandled promise rejections and send report to Bugsnag
const globalPromiseRejectionHandler = (event) => {
  Bugsnag.notify(event);
  // Prevent outputting the error to the console
  event.preventDefault();
};

const initBugsnag = (productName) => {
  const BUGSNAG_API_KEY = process.env[`VUE_APP_${productName.toUpperCase()}_BUGSNAG_API_KEY`];
  const RELEASE_VERSION = process.env.VUE_APP_RELEASE || '';
  console.log('[bugsnag] App version:', RELEASE_VERSION); // [SU-4169] need this to check if Bugsnag gets app version from cli
  if (process.env.NODE_ENV === 'production' && BUGSNAG_API_KEY) {
    const options = {
      apiKey: BUGSNAG_API_KEY,
      plugins: [new BugsnagPluginVue()],
      onError (event) {
        if (event.originalError instanceof AxiosError) return false;
      },
    };

    if (RELEASE_VERSION.length) {
      options.appVersion = RELEASE_VERSION;
    }
    Bugsnag.start(options);
    Bugsnag.getPlugin('vue').installVueErrorHandler(Vue);
    window.onunhandledrejection = globalPromiseRejectionHandler;
  }
};

const initGTM = gtmCode => {
  if (process.env.NODE_ENV === 'production') {
    Vue.use(VueGtm, {
      id: gtmCode,
      enabled: false,
    });
  }
};

/**
 * App Create
 * @param {string} productName - Product name. Is needed to initialize specific constants, helpers, inputs limits, validation rules etc.
 * @param {Object} additionalData - May contain product version or additional data for Leadgen products' json configs. Is passed to `provide` section of the root App.
 */
export const createApp = async (productName, additionalData = {}) => {
  const apiProductName = additionalData?.options?.apiProductName || productName;
  const isLeadgen = additionalData.isLeadgen;
  Vue.prototype.$api = await createApi(apiProductName);
  Vue.config.productionTip = false;
  Vue.config.devtools = process.env.NODE_ENV !== 'production';
  // add Bugsnag on production environment
  if (process.env.NODE_ENV === 'production') {
    Vue.prototype.$Bugsnag = Bugsnag;
  }

  try {
    const config = await getConfig(productName);

    registerVueGlobals(config, productName);

    initBugsnag(productName);
    initGTM(config.gtmCode);
    initRumTracker(config);
    initPartnerFonts(config);

    const [
      constants,
      inputCharsLimiters,
      helpers,
    ] = await Promise.all([
      getConstants(productName),
      getLimiters(productName),
      getHelpers(productName),
    ]);
    initValidation({
      productName,
      constants,
      isLeadgen,
    });
    // Generate paths if form meta data exists and push it to additionalData
    if (additionalData.formMetadata?.steps) {
      additionalData.formMetadataPaths = additionalData.formMetadata.steps.reduce((stepsAcc, step) => ({
        ...stepsAcc,
        [step.stepId]: step.questions.reduce((questionsAcc, question) => ({
          ...questionsAcc,
          [question.questionId]: question,
        }), {}),
      }), {});

      // hack for core/leadgen same name products
      const configProductName = additionalData?.buildProductName || productName;

      additionalData.formMetadata = await usePartnerConfig(configProductName, additionalData, config);
    }

    new Vue({
      store,
      router: initRouter(productName, isLeadgen),
      vuetify: initVuetify(config),
      provide: {
        productName,
        config,
        constants,
        inputCharsLimiters,
        helpers,
        apiProductName,
        ...additionalData.options,
        ...additionalData,
      },
      render: h => h(App),
    }).$mount('#app');
  } catch (error) {
    console.warn(error);
  }
};
